import { apiFetch, type JsonPatchOperation } from '@graal/api';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import type { CatalogPrincipal } from './catalog-api';

/**
 * Glossaire métier du locataire — tag `glossary` de `merged.yaml` (contrat 929e8437, 2026-10-19) :
 * `findGlossaryTerms`, `createGlossaryTerm`, `findGlossaryTermById`, `updateGlossaryTerm`,
 * `deleteGlossaryTerm`, `findGlossaryTermAssets`. Lecture sous `/catalog/read` sur le locataire,
 * écriture sous `/catalog/edit` ; un actif lié n'est rendu que si l'appelant lit sa couche.
 *
 * Client `@graal/api` (`apiFetch`), types aux noms des schémas du contrat, validés par
 * `catalog-contract.test.ts` — même raison que `catalog-api.ts`.
 */

/** `GlossaryTerm` du contrat. `name` est unique dans le locataire (409 sinon). */
export type GlossaryTerm = {
  id?: string;
  name?: string;
  description?: string;
  synonyms?: string[];
  owner?: CatalogPrincipal;
  created?: string;
  updated?: string;
};

/** `GlossaryTermAsset` : une table ou une colonne qui porte le terme. */
export type GlossaryTermAsset = {
  kind?: 'table' | 'field';
  layer_id?: string;
  layer?: string;
  database?: string;
  table?: string;
  field?: string;
  qualified_name?: string;
};

/** Le média type d'écriture du glossaire (`application/vnd.graal.systems.v1.glossaryterm+json`). */
export const GLOSSARY_RESOURCE = 'glossaryterm';
export const GLOSSARY_LIMIT = 500;

const termUrl = (id: string) => `/glossary/terms/${encodeURIComponent(id)}`;

export const listGlossaryTerms = (search: string, signal?: AbortSignal) =>
  apiFetch<GlossaryTerm[]>({
    url: '/glossary/terms',
    method: 'GET',
    params: { ...(search ? { search } : {}), limit: GLOSSARY_LIMIT },
    signal,
  });

export const getGlossaryTerm = (id: string, signal?: AbortSignal) =>
  apiFetch<GlossaryTerm>({ url: termUrl(id), method: 'GET', signal });

export const createGlossaryTerm = (term: GlossaryTerm) =>
  apiFetch<GlossaryTerm>({ url: '/glossary/terms', method: 'POST', data: term, resource: GLOSSARY_RESOURCE });

export const patchGlossaryTerm = (id: string, operations: JsonPatchOperation[]) =>
  apiFetch<GlossaryTerm>({ url: termUrl(id), method: 'PATCH', data: operations });

/** 202 sans corps ; les liens du terme vers les tables et les colonnes partent avec lui. */
export const deleteGlossaryTerm = (id: string) => apiFetch<void>({ url: termUrl(id), method: 'DELETE' });

export const listGlossaryTermAssets = (id: string, signal?: AbortSignal) =>
  apiFetch<GlossaryTermAsset[]>({ url: `${termUrl(id)}/assets`, method: 'GET', signal });

export const glossaryKeys = {
  all: (tenant: string) => [tenant, 'glossary'] as const,
  list: (tenant: string, search: string) => [tenant, 'glossary', 'list', search] as const,
  term: (tenant: string, id: string) => [tenant, 'glossary', 'term', id] as const,
  assets: (tenant: string, id: string) => [tenant, 'glossary', 'term', id, 'assets'] as const,
};

export const glossaryTermsQuery = (tenant: string, search = '') =>
  queryOptions({
    queryKey: glossaryKeys.list(tenant, search),
    queryFn: ({ signal }) => listGlossaryTerms(search, signal),
    placeholderData: keepPreviousData,
  });

export const glossaryTermQuery = (tenant: string, id: string) =>
  queryOptions({
    queryKey: glossaryKeys.term(tenant, id),
    queryFn: ({ signal }) => getGlossaryTerm(id, signal),
  });

export const glossaryTermAssetsQuery = (tenant: string, id: string) =>
  queryOptions({
    queryKey: glossaryKeys.assets(tenant, id),
    queryFn: ({ signal }) => listGlossaryTermAssets(id, signal),
  });

export const glossaryHref = {
  list: (tenant: string) => `/${tenant}/glossary`,
  create: (tenant: string) => `/${tenant}/glossary/new`,
  term: (tenant: string, id: string) => `/${tenant}/glossary/${encodeURIComponent(id)}`,
};

/** Onglets du détail d'un terme ; l'onglet actif vit dans l'URL (`?tab=`). */
const GLOSSARY_TABS = ['summary', 'assets', 'definition'] as const;
export type GlossaryTab = (typeof GLOSSARY_TABS)[number];
export const isGlossaryTab = (value: unknown): value is GlossaryTab =>
  typeof value === 'string' && (GLOSSARY_TABS as readonly string[]).includes(value);
